import {
  Component, HostBinding, Input, Output, EventEmitter, OnInit, OnDestroy, inject, computed, signal, HostListener,
} from '@angular/core';
import { Location } from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';

import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxToolbarModule } from 'devextreme-angular/ui/toolbar';
import { TranslateModule } from '@ngx-translate/core';

import { UserPanelComponent } from '../user-panel/user-panel.component';
import { AuthService, IUser } from 'src/app/services';
import { ThemeSwitcherComponent } from 'src/app/components/library/theme-switcher/theme-switcher.component';
import { LanguageSwitcherComponent } from 'src/app/components/library/language-switcher/language-switcher.component';
import { ChatNotificationsService } from 'src/app/shared/services/chat-notifications.service';
import { AppIconComponent } from 'src/app/shared/components/app-icon/app-icon.component';
import { CommandPaletteService } from 'src/app/shared/services/command-palette.service';
import { BreadcrumbService } from 'src/app/shared/services/breadcrumb.service';
import { HeaderActionsService } from 'src/app/shared/services/header-actions.service';

@Component({
  selector: 'app-header',
  templateUrl: 'app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  imports: [
      CommonModule,
      FormsModule,
      RouterModule,
      DxButtonModule,
      DxToolbarModule,
      ThemeSwitcherComponent,
      LanguageSwitcherComponent,
      UserPanelComponent,
      TranslateModule,
      AppIconComponent,
  ]
})

export class AppHeaderComponent implements OnInit, OnDestroy {
  @Output()
  menuToggle = new EventEmitter<boolean>();

  @Input()
  menuToggleEnabled = false;

  @Input()
  title!: string;

  /** Sidebar collapsado (icon-only). El padre lo bindea desde `!menuOpened`. */
  @Input() compactMode = false;

  /** Marca el host para que el SCSS aplique tokens de logo reducidos en compactMode. */
  @HostBinding('class.is-compact') get hostCompact() { return this.compactMode; }

  private authService = inject(AuthService);
  private router = inject(Router);
  private location = inject(Location);
  protected notifs = inject(ChatNotificationsService);
  /** Command Palette singleton — usado por el search-trigger del toolbar. */
  protected commandPalette = inject(CommandPaletteService);
  /** Breadcrumb dinámico — deriva del URL + NavigationService. */
  protected breadcrumb = inject(BreadcrumbService);
  /** Slot de actions contextuales que cada página puede llenar via signal. */
  protected headerActions = inject(HeaderActionsService);

  /** Click en el icono campana toggle el push de browser notifications.
   *  Si nunca se pidio permission, dispara el prompt nativo. */
  toggleBrowserNotifs = async () => {
    if (this.notifs.browserPermission() === 'default') {
      await this.notifs.requestBrowserPermission();
    }
    this.notifs.togglePush();
  };

  toggleSoundNotifs = () => this.notifs.toggleSound();

  user: IUser | null = null;
  /** Total de no-leídos del chat. Se calcula contra `ChatNotificationsService`
   *  que escucha al hub real (no del mock). */
  totalChatNoLeidos = computed(() => this.notifs.totalNoLeidos());

  isMac = signal(typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform));

  /** Estado de conexion del browser — dot verde en el avatar cuando hay red.
   *  Listener de online/offline events lo mantiene en sync. */
  isOnline = signal(typeof navigator !== 'undefined' ? navigator.onLine : true);

  /** Header colapsado cuando el contenido scrollea — reduce padding/brand. */
  isScrolled = signal(false);

  /** Conteo de navegaciones que hemos hecho — usado para decidir si "back" tiene
   *  sentido (no podemos saber si el browser puede ir back fuera de la app). Si
   *  el contador es 0 en sesion, no hubo nav previa: deshabilitamos el boton. */
  private navCount = 0;
  canGoBack = signal(false);

  /** Bindings de listeners online/offline para poder removerlos en destroy. */
  private onlineListener = () => this.isOnline.set(true);
  private offlineListener = () => this.isOnline.set(false);

  irAlChat = () => {
    this.router.navigate(['/chat']);
  };

  /** El "search" del toolbar abre el Command Palette global — no es un input
   *  de búsqueda local. Patrón Linear/Vercel: un solo lugar para jump-to-anything
   *  y comandos. El listener global ⌘K/Ctrl+K vive en CommandPaletteService. */
  openCommandPalette() {
    this.commandPalette.open();
  }

  /** Atras en el historial del browser. Solo invocable si hubo al menos 1
   *  navegacion previa en esta sesion — sino history.back() podria sacar al
   *  user de la app o no hacer nada. */
  goBack(): void {
    if (this.canGoBack()) this.location.back();
  }

  /** Adelante en el historial. No podemos saber si el browser puede ir forward
   *  (no esta expuesto en el API), asi que lo dejamos siempre habilitado. Si
   *  no hay forward, history.forward() es no-op. */
  goForward(): void {
    this.location.forward();
  }

  /** Atajos Ctrl/⌘ + [ y ] — patron de browsers (Chrome, Firefox, Safari) y
   *  apps como Linear, Notion. Capturamos en el window porque cualquier focus
   *  del usuario es valido para el shortcut. */
  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(e: KeyboardEvent): void {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (e.key === '[') {
      e.preventDefault();
      this.goBack();
    } else if (e.key === ']') {
      e.preventDefault();
      this.goForward();
    }
  }

  /** Scroll-aware: comprimir el header cuando el usuario scrollea > 8px en el
   *  contenido. Listener pasivo en window para no bloquear el scroll smooth.
   *  Las paginas que usan dx-scroll-view scrollean su propio container, no el
   *  window — en esas, el header no se comprime (no rompe, queda en estado
   *  default). Para cubrir ese caso futuro, habria que escuchar capture en
   *  document con `passive: true`. Por ahora arrancamos con window-scroll. */
  @HostListener('window:scroll')
  onWindowScroll(): void {
    const y = typeof window !== 'undefined' ? window.scrollY : 0;
    const next = y > 8;
    if (next !== this.isScrolled()) this.isScrolled.set(next);
  }

  /** Bind al host: SCSS aplica `.is-scrolled` cuando hay scroll. */
  @HostBinding('class.is-scrolled') get hostScrolled() { return this.isScrolled(); }

  /**
   * Items del menú del avatar. ARRAY ESTABLE — calculado UNA vez en ngOnInit y
   * re-calculado solo cuando cambia el user (login/logout/restoreSession).
   *
   * NO usar getter: devolvería un array nuevo en cada change detection, y la
   * `dx-list` de DevExtreme re-renderiza los items + sus handlers a referencias
   * stale, rompiendo los clicks. Con array estable, `onItemClick` dispara
   * correctamente y el `onClick` se ejecuta.
   */
  userMenuItems: { text: string; icon: string; onClick: () => void }[] = [];

  private rebuildUserMenu(): void {
    const tieneChangelog = this.authService.hasPermission('changelog:view');
    this.userMenuItems = [
      {
        text: 'Mi perfil',
        icon: 'user',
        onClick: () => { this.router.navigate(['/user-profile']); },
      },
      ...(tieneChangelog ? [{
        text: 'Changelog',
        icon: 'event',
        onClick: () => { this.router.navigate(['/changelog']); },
      }] : []),
      {
        text: 'Cerrar sesión',
        icon: 'runner',
        onClick: () => { this.authService.logOut(); },
      },
    ];
  }

  ngOnInit() {
    this.rebuildUserMenu();
    this.refreshUser();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.refreshUser();
        // Habilitar boton "atras" cuando hay al menos 1 nav previa en sesion.
        // Conteo incremental — el primer NavigationEnd es la nav de boot, las
        // siguientes son navegaciones reales.
        this.navCount++;
        if (this.navCount >= 2) this.canGoBack.set(true);
      });
    // Reconstruir el menú después de cada login (los claims cambian — ej. al
    // re-loguearse como admin aparece "Changelog", o se ocultan items según rol).
    this.authService.loggedIn$.subscribe(() => this.rebuildUserMenu());

    // Online/offline listeners — actualizan el dot del avatar en tiempo real.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineListener);
      window.addEventListener('offline', this.offlineListener);
    }
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      window.removeEventListener('offline', this.offlineListener);
    }
  }

  private async refreshUser() {
    const res = await this.authService.getUser();
    this.user = res.data ?? null;
    // Rebuild también acá por si el user cambió (ej. impersonation futura)
    // o el primer refreshUser corrió antes de tener claims.
    this.rebuildUserMenu();
  }

  toggleMenu = () => {
    this.menuToggle.emit();
  };
}
